import { useEffect, useState } from 'react';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { IMenuObjectWithDaypart, getMenuItemDayParts } from 'utils/availability';
import { logger } from 'utils/logger';

import { useCartEntriesMenuObjects } from './use-cart-entries-menu-objects';

/**
 * Iterates over menu items and picks unique day parts
 */
const getAllMenuItemDayParts = (menuItems: IMenuObjectWithDaypart[]) =>
  menuItems.reduce((acc: string[], menuItem) => {
    const menuItemDayParts = getMenuItemDayParts(menuItem);
    return acc.concat(menuItemDayParts.filter(dayPart => !acc.includes(dayPart)));
  }, []);

/**
 * Gets unique day parts from unified menu entries based on cart entries
 * @param cartEntries - Legacy cart entries
 * @param indexedMenu - Indexed unified menu
 * @returns Unique day parts based on cart entries
 */
const getUnifiedMenuEntriesUniqueDayParts = (
  cartEntries: ILegacyCartEntry[],
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>
) => {
  return cartEntries.reduce<string[]>((acc, entry) => {
    const menuEntry = indexedMenu.find(entry._id || entry.sanityId);

    if (!menuEntry?.dayParts) {
      logger.debug({
        message: '[useCartEntriesDayParts] Menu entry not found or its day parts are not available',
        itemId: entry._id || entry.sanityId,
        itemName: entry.name,
        itemType: entry.type,
      });

      return acc;
    }

    return acc.concat(
      menuEntry.dayParts
        .map(itemDayPart => itemDayPart.toLowerCase()) // Match legacy menu item day parts format
        .filter(itemDayPart => !acc.includes(itemDayPart))
    );
  }, []);
};

export const useCartEntriesDayParts = (cartEntries: ILegacyCartEntry[] | undefined) => {
  const { activeDayParts } = useMainMenuContext();
  const { getCartEntriesMenuObjects } = useCartEntriesMenuObjects();

  const { indexedMenu } = useUnifiedMenuContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const [isLoading, setIsLoading] = useState(true);
  const [cartEntriesDayParts, setCartEntriesDayParts] = useState<string[]>([]);
  const [cartEntriesActiveDayParts, setCartEntriesActiveDayParts] = useState<IDayPartBoundary[]>(
    []
  );

  useEffect(() => {
    if (!cartEntries) {
      return;
    }

    const fetchData = async () => {
      let menuItemsDayParts: string[] = [];

      if (isUnifiedMenuEnabled) {
        menuItemsDayParts = getUnifiedMenuEntriesUniqueDayParts(cartEntries, indexedMenu);
      } else {
        const data = await getCartEntriesMenuObjects(cartEntries);
        // FIXME: unsafe cast - Migrate to updated queries
        // @ts-ignore
        menuItemsDayParts = getAllMenuItemDayParts(data.map(entry => entry.data));
      }

      const menuItemsActiveDayParts = activeDayParts.filter(dayPart =>
        menuItemsDayParts.includes(dayPart.key.toLowerCase())
      );

      setCartEntriesDayParts(menuItemsDayParts);
      setCartEntriesActiveDayParts(menuItemsActiveDayParts);
      setIsLoading(false);
    };

    fetchData();
  }, [activeDayParts, cartEntries, getCartEntriesMenuObjects, indexedMenu, isUnifiedMenuEnabled]);

  return {
    cartEntriesDayParts,
    cartEntriesActiveDayParts,
    isLoading,
  };
};
